import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, Renderer2, effect, input, output, DOCUMENT } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { DynamicMenuCollapseComponent } from 'src/app/components/dynamic-menu-collapse/dynamic-menu-collapse.component';
import { SearchBarComponent } from 'src/app/components/search-suggestion/components/search-bar/search-bar.component';
import { StaticMenuCollapseComponent } from 'src/app/components/static-menu-collapse/static-menu-collapse.component';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { Cart } from 'src/app/core/services/cart/cart-api.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { safeCartSetter } from 'src/app/core/utilities/cart-fallback.util';
import { createDefaultCart, createDefaultHTMLElement, createDefaultHeaderData } from 'src/app/core/utilities/type-defaults.util';
import { Category } from 'src/app/shared/services/common-api.service';
import { HeaderLayout } from '../../dynamic-header.component';
import { MenuCollapseService } from '../../services/menu-collapse.service';
import { DynamicHeaderCommon } from '../dynamic-header-common.class';
import { HeaderData } from '../header-data';

@Component({
  selector: 'category-layout-12',
  standalone: true,
  templateUrl: './layout-12.component.html',
  styleUrls: ['./layout-12.component.scss'],
  imports: [CommonModule, RouterLink, StaticMenuCollapseComponent, DynamicMenuCollapseComponent, SearchBarComponent]
})
export class Layout12Component extends DynamicHeaderCommon implements AfterViewInit, OnDestroy {

  readonly dynamicHeaderInline = input<boolean>(false);
  readonly dynamicHeaderHover = input<boolean>(false);
  readonly domain = input<string>('');
  readonly categoryInput = input<Category[] | null>(null, { alias: 'category' });
  readonly cartInput = input<Cart | null>(null, { alias: 'cart' });
  readonly layoutTypeInput = input<string>('', { alias: 'layoutType' });
  readonly selectedDeviceTypeInput = input<string>('', { alias: 'selectedDeviceType' });
  readonly clickedElementAvailableInput = input<HTMLElement | null>(null, { alias: 'clickedElementAvailable' });
  readonly headerDataInput = input<HeaderData | null>(null, { alias: 'headerData' });

  readonly categoryHeaderStyle$ = output<string>();

  IMAGE_URLS = IMAGE_URLS;
  HeaderLayout = HeaderLayout;
  UTM_MEDIUM = 'menu';

  // Getters for backward compatibility
  get category(): Category[] | null {
    return this._category;
  }
  protected _category!: Category[] | null;

  get cart(): Cart {
    return this._cart;
  }
  protected _cart: Cart = createDefaultCart();

  get layoutType(): string {
    return this._layoutType;
  }
  protected _layoutType = '';

  get clickedElementAvailable(): HTMLElement {
    return this._clickedElementAvailable;
  }
  protected _clickedElementAvailable: HTMLElement = createDefaultHTMLElement() as HTMLElement;

  override get headerData(): HeaderData {
    return this._headerData;
  }
  protected override _headerData: HeaderData = createDefaultHeaderData();

  isSubmenuAvailable = true;
  override showSubcategoryPanel: boolean = false;
  override searchInputControl = new UntypedFormControl('');

  constructor(
    private router: Router,
    protected override renderer: Renderer2,
    protected override elementRef: ElementRef,
    protected override platformService: PlatformService,
    protected override menuCollapseService: MenuCollapseService,
    @Inject(DOCUMENT) protected override document: Document,
  ) {
    super(
      renderer,
      menuCollapseService,
      elementRef,
      platformService,
      document,
      new BrowserApiService()
    );

    effect(() => {
      const headerDataValue = this.headerDataInput();
      if (headerDataValue) {
        this._headerData = headerDataValue;
      }
    });

    effect(() => {
      const categoryValue = this.categoryInput();
      this._category = categoryValue;
    });

    effect(() => {
      const cartValue = this.cartInput();
      this._cart = safeCartSetter(cartValue);
    });

    effect(() => {
      const layoutTypeValue = this.layoutTypeInput();
      this._layoutType = layoutTypeValue;
    });

    // Device type effect - now handled directly in template via selectedDeviceTypeInput()
    effect(() => {
      const deviceTypeValue = this.selectedDeviceTypeInput();
      if (deviceTypeValue === this.device.desktop) {
        this.isHamburgerMenu = false;
        this.menuCollapseService.setHamburgerMenu(false);
        this.showMobileSearchPanel = false;
      }
      this.repositionHeaderMenu();
    });

    effect(() => {
      const clickedElementValue = this.clickedElementAvailableInput();
      if (clickedElementValue) {
        this._clickedElementAvailable = clickedElementValue;
      } else {
        this._clickedElementAvailable = createDefaultHTMLElement() as HTMLElement;
      }
    });
  }
  private hoverTimeoutId: ReturnType<typeof setTimeout> | null = null;
  ngAfterViewInit(): void {
    this.repositionHeaderMenu();
    this.hoverTimeoutId = setTimeout(() => {
      this.addHoverForMenu();
    }, 2000);
    this.applyFocusEvent();
  }

  onLinkClick(event: Event): void {
    const subMenus = document.getElementsByClassName("hide-sub-menu-header");
    // Iterate over the elements and set opacity to 0
    Array.from(subMenus).forEach(subMenu => {
      this.renderer.setStyle(subMenu, 'display', 'none');
    });
  }
  onMenuMouseEnter(menuId: string): void {
    this.showActiveColor = true;
    this.currentMenuId = menuId;

  }
  filterProductsByCategory(category_url_path: string) {
    this.router.navigate([`/category/${category_url_path ?? ''}`], {
      queryParams: {
        utm_source: 'menu',
        utm_medium: category_url_path
      }
    })
  }

  override toggleSearchDropDown() {
    this.isSearchOverlay = !this.isSearchOverlay;
    this.menuCollapseService.setHamburgerMenu(false);
    this.searchInputControl.reset();
    if (this.isSearchOverlay) {
      // Prevent body scroll
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      this.renderer.setStyle(this.document.body, 'position', 'fixed');
      this.renderer.setStyle(this.document.body, 'width', '100%');
      this.renderer.setStyle(this.document.body, 'height', '100%');
    } else {
      // Restore body scroll
      this.renderer.removeStyle(this.document.body, 'overflow');
      this.renderer.removeStyle(this.document.body, 'position');
      this.renderer.removeStyle(this.document.body, 'width');
      this.renderer.removeStyle(this.document.body, 'height');
    }
  }

  override toggleHamburgerMenu() {
    this.isToggled = true;
    this.menuCollapseService.toggleHamburgerMenu();
    this.isHamburgerMenu = !this.isHamburgerMenu;
    this.isSearchOverlay = false;
    if (this.isHamburgerMenu) {
      // Prevent body scroll
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      this.renderer.setStyle(this.document.body, 'position', 'fixed');
      this.renderer.setStyle(this.document.body, 'width', '100%');
      this.renderer.setStyle(this.document.body, 'height', '100%');
    } else {
      // Restore body scroll
      this.renderer.removeStyle(this.document.body, 'overflow');
      this.renderer.removeStyle(this.document.body, 'position');
      this.renderer.removeStyle(this.document.body, 'width');
      this.renderer.removeStyle(this.document.body, 'height');
    }
  }

  searchItems() {
    this.toggleSearchDropDown()
  }

  subMenu() {
    this.isSubmenuAvailable = true;
  }

  toggleMenu() {
    this.showSubcategoryPanel = !this.showSubcategoryPanel;
  }
  ngOnDestroy(): void {
    if (this.hoverTimeoutId) {
      clearTimeout(this.hoverTimeoutId);
    }
  }
}
